import React, { useState, useRef, useEffect, useMemo } from "react";
import { graphql, navigate } from 'gatsby';
import { Auth } from '@aws-amplify/auth';
import { authStorage } from '@services/amplifyStorage';
import { RichText } from "prismic-reactjs";
import { Button, DialogContent, DialogActions, Alert, TextField, Password } from '@lesmills-international/components';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import styled from 'styled-components';
import { ApolloError } from "@apollo/client";
import { useFlags } from 'gatsby-plugin-launchdarkly';
import { createPublicClient } from '../services/client';
import getUserSubscription from '../graphql/getUserSubscription';
import ValidateSignedLink from '../graphql/validateSignedLink';
import useGetCountryCode from "../hooks/useGetCountryCode";
import SignupFederatedUserWithSubscription from '../graphql/signupFederatedUserWithSubscription';
import SignupResellerUserWithSubscription from "../graphql/signupResellerUserWithSubscription";
import doesUserExist from '../graphql/doesUserExist';
import submitUserPreferences from '../utils/submitUserPreferences';
import Layout from '../components/common/layout';
import Title from '../components/common/title';
import RadioGroup from '../components/common/radioGroup';
import RecaptchaV3 from '../components/common/recaptchaV3';
import { Link } from '../../../gatsby-theme-engagement/src/components/atoms';
import { Subtitle, Wrapper } from '../components/signup/createAccount/style';
import ROUTES from '../../../../src/constants/routes';

import {
  addErrorInDatadogRum,
  isOptInNewsletterCountry,
  setLocalStorage,
  deleteSessionStorage,
  getSessionStorage,
  getAutomationRecaptcha,
  unpackSignedLink,
} from '../utils/utilities';

interface IError {
  [key: string]: string;
}

type SignedLink = {
  hostname: string;
  path: string;
  email: string;
  accessId: string;
  productId: string;
  externalId: string;
  timestamp: string;
  expiryDays: string;
  guid: string;
  signature: string;
}

interface FormValues {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  marketingSubscribed: string;
  version?: number;
}

interface PopupState {
  visible: boolean;
  title: string;
  subtitle: string;
}

interface FederatedUser {
  given_name?: string;
  family_name?: string;
  email?: string;
  identities?: Array<{ providerType: string }>;
}

interface PageContext {
  lang: string;
  isFederatedSignUp: boolean;
  version: number;
}

interface PageData {
  prismicSignUpLayout: any;
  prismicCreateResellerAccountPage: {
    data: any;
  };
}

interface Location {
  state?: {
    signInEmail?: string;
    signedLink?: SignedLink;
  };
  href: string;
  pathname: string;
  search: string;
}

const VALIDATOR = {
  EMAIL: /^(\w+[\w-+\\.]*)@(\w+([-\\.]\w+)*\.[a-zA-Z]{2,})$/,
  PASSWORD: /^.{7,}$/,
};
const IS_FEDERATED_ACCOUNT = "is_federated_account";
const RESELLER_ACCESS_ID = "reseller_access_id";

export const ReceiveEmailLabel = styled.p`
  width: 320px;
  text-align: left;
  font-size: 12px;
  line-height: 18px;
  color: ${(props) => props.theme.colors.darkGrey};
`;

const LoginLink = styled.div`
  text-align: center;
  font-size: 14px;
  line-height: 18px;
  margin-bottom: 24px;
  color: ${(props) => props.theme.colors.darkGrey};
  & a {
    text-decoration: underline;
    color: ${(props) => props.theme.colors.darkGrey};
  }
`;

const Form = styled(Box)(() => ({
  '& .MuiTextField-root': { marginBottom: '15px', width: '320px' },
  // maxWidth: '320px',
  textAlign: 'center',

  '& .MuiInputBase-input:focus': { boxShadow: 'none' },
  '& a': { textDecoration: 'underline' }
}));

const StyledDialog = styled(Dialog)(() => ({
  '& .MuiIconButton-root': { visibility: 'hidden' }
}));

const CreateResellerAccountPage = ({ 
  pageContext, 
  data, 
  location 
}: { 
  pageContext: PageContext; 
  data: PageData; 
  location: Location;
}) => {
  const { prismicSignUpLayout, prismicCreateResellerAccountPage: { data: rpData } } = data;
  const { lang, isFederatedSignUp, version } = pageContext;
  const { countryCode } = useGetCountryCode();
  const { rtDataConsentCheckbox } = useFlags();
  const recaptchaAutomation = getAutomationRecaptcha();
  const [isProcessing, setIsProcessing] = useState(false);
  const [email, setEmail] = useState("");
  const [isDisableEmail, setDisableEmail] = useState(false);
  const [serverError, setServerError] = useState("");
  const [popup, setPopup] = useState<PopupState>({ visible: false, title: "", subtitle: "" });
  const [federatedError, setFederatedError] = useState("");
  const isOptIn = useMemo(() => isOptInNewsletterCountry(), []);
  const [marketingSubscribed, setMarketingSubscribed] = useState(
    isOptIn ? "yes" : ""
  );
  const [federatedSignInUser, setFederatedSignInUser] = useState<FederatedUser>({});
  const [internalError, setInternalError] = useState<IError>({
    email: "",
    password: "",
    firstName: "",
    lastName: "",
    marketingSubscribed: "",
  });

  const firstNameRef = useRef<HTMLInputElement>(null);
  const lastNameRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const recaptchaRef = useRef<any>(null);
  const accessId = getSessionStorage(RESELLER_ACCESS_ID);

  const currentPagePath = typeof window !== 'undefined' ? window.location.pathname : '';
  const signedLink = unpackSignedLink(location) || location.state?.signedLink;

  const configAmplify = (isFederated: boolean) =>
    Auth.configure({
      storage: authStorage,
      region: process.env.GATSBY_AWS_COGNITO_REGION,
      userPoolId: process.env.GATSBY_AWS_COGNITO_USER_POOL_ID,
      userPoolWebClientId: process.env.GATSBY_AWS_COGNITO_USER_POOL_WEB_CLIENT_ID,
      graphql_endpoint_iam_region: process.env.GATSBY_AWS_COGNITO_REGION,
      graphql_endpoint: process.env.GATSBY_APP_SYNC_URL,
      Auth: {
        userPoolWebClientId: process.env.GATSBY_AWS_COGNITO_USER_POOL_WEB_CLIENT_ID,
        authenticationFlowType: 'USER_PASSWORD_AUTH',
      },
      API: isFederated
        ? {
            graphql_headers: async () => ({
              Authorization: (await Auth.currentSession())
                .getIdToken()
                .getJwtToken(),
            }),
            graphql_endpoint_iam_region: process.env.GATSBY_AWS_COGNITO_REGION,
            graphql_endpoint: process.env.GATSBY_APP_SYNC_URL,
          }
        : {
            graphql_headers: async () => ({}),
          },
    });
  
  const showPopupErrorMessage = (errCode: string) => {
    const errorCode = errCode.toUpperCase();
    if (errorCode === "LINK_EXPIRED") {
      setPopup({
        visible: true,
        title: rpData.error_popup_title_link_expired.text,
        subtitle: rpData.error_popup_subtitle_link_expired.text,
      });
    } else if (errorCode === "LINK_REDEEMED") {
      setPopup({
        visible: true,
        title: rpData.error_popup_title_link_redeemed.text,
        subtitle: rpData.error_popup_subtitle_link_redeemed.text,
      });
    } else {
      setPopup({
        visible: true,
        title: rpData.error_popup_title_link_invalid.text,
        subtitle: rpData.error_popup_subtitle_link_invalid.text,
      });
    }
  };
  
  useEffect(() => {
    configAmplify(isFederatedSignUp);
    if (isFederatedSignUp) {
      Auth &&
        Auth.currentAuthenticatedUser()
          .then(async res => {
            const federatedSignInUserPayload =
              res.signInUserSession && res.signInUserSession.idToken.payload

            setFederatedSignInUser(federatedSignInUserPayload)
            if (
              federatedSignInUserPayload.identities[0].providerType === "OIDC"
            ) {
              setLocalStorage(IS_FEDERATED_ACCOUNT, true)
              const userInfo = await createPublicClient.query({ query: getUserSubscription });
              const vimeoUserTicket =
                userInfo &&
                userInfo.data.getUser?.userEntitlements?.LMOD?.vimeoUserTicket
              if (vimeoUserTicket) {
                navigate(
                  `${process.env.GATSBY_WATCH_LES_MILLS_URL}/browse?ticket=${vimeoUserTicket}`
                )
                
              }
            }
          })
          .catch(() => {
            setFederatedError(rpData.not_authenticated_error.text)
          })
      if (!accessId || (accessId && accessId.length === 0)) {
        setFederatedError(rpData.empty_access_id_error.text)
      }
    } else {
      const validateSignedLink = async (sLink: SignedLink) => {
        try {
          const res = await createPublicClient.query({ 
            query: ValidateSignedLink, 
            variables: { signed_link: sLink, version } 
          });
          const result = res.data.validateSignedLink;
          const { valid, error, email: eResult, isEmailEditable } = result || {};
          
          if (!valid) {
            showPopupErrorMessage(error || "LINK_INVALID");
          }
          if (eResult && eResult !== "") {
            setEmail(eResult);
          }
          if (isEmailEditable === false) {
            setDisableEmail(true);
          }
        } catch (err: any) {
          addErrorInDatadogRum(err);
          setServerError(err.message || "An error occurred while validating the link");
        }
      };
      
      // verify the signed link in case not federated Sign In
      if (signedLink) {
        validateSignedLink(signedLink as SignedLink);
      } else if (!signedLink) {
        setPopup({
          visible: true,
          title: rpData.error_popup_title_link_invalid.text,
          subtitle: rpData.error_popup_subtitle_link_invalid.text,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formValues = {
    email: () =>
      emailRef.current?.value ? emailRef.current.value.trim().toLowerCase() : "",
    password: () =>
      passwordRef.current?.value ? passwordRef.current.value.trim() : "",
    firstName: () =>
      firstNameRef.current?.value ? firstNameRef.current.value.trim() : "",
    lastName: () =>
      lastNameRef.current?.value ? lastNameRef.current.value.trim() : "",
    marketingSubscribed: () => marketingSubscribed,
  };

  const validators = {
    email: (em: string) =>
      em
        ? VALIDATOR.EMAIL.test(em)
          ? ""
          : rpData.email_invalid.text
        : rpData.email_required.text,
    password: (password: string) =>
      password
        ? VALIDATOR.PASSWORD.test(password)
          ? ""
          : rpData.password_invalid.text
        : rpData.password_required.text,
    firstName: (firstName: string) =>
      firstName
        ? ""
        : rpData.first_name_required.text,
    lastName: (lastName: string) =>
      lastName ? "" : rpData.last_name_required.text,
    marketingSubscribed: (sub: string) =>
      sub ? "" : rpData.marketing_subscription_required.text,
  };

  const handleDisableLink = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (isProcessing) {
      e.preventDefault();
    }
  };

  const handleOnKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit(e as any);
    } else {
      handleOnChange(e as any);
    }
  };

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let name = e.target.getAttribute("id") || "";

    if (name === "receive-email-radio-group") {
      setMarketingSubscribed(e.target.value);
      name = "marketingSubscribed";
    }

    // Only validate after clicking submit once
    if (internalError[name]) {
      const value = formValues[name as keyof typeof formValues]();
      const error = validators[name as keyof typeof validators](value);
      if (error !== internalError[name]) {
        setInternalError({
          ...internalError,
          [name]: error,
        });
      }
    }
  };

  const handleReceiveEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const optIn = e.target.value; // Is either "yes" or "no"
      setMarketingSubscribed(optIn);
    } catch (err) {
      addErrorInDatadogRum(err);
    }
  };

  const handleValidateRecaptcha = async () => {
    let validationData: { Name: string; Value: string } | undefined;
    let captchaSignature: { auth: string; nonce: string } | undefined;

    if (recaptchaAutomation) {
      validationData = {
        Name: 'recaptchaToken',
        Value: '',
      };
      captchaSignature = {
        auth: recaptchaAutomation.auth,
        nonce: recaptchaAutomation.nonce,
      };
    } else if (recaptchaRef?.current) {
      const token = await recaptchaRef.current.execute();
      validationData = {
        Name: 'recaptchaToken',
        Value: token,
      };
    }
    return {
      validationData,
      captchaSignature,
    };
  };

  // LPB-1642: read from the submitted form values — the same source `subComms` on the
  // signup mutation is built from — so C.io and Didomi can never receive different
  // consent for the same signup (LPB-1591). The reseller form has no data-consent
  // checkbox, so pltvAnalyticsAgreed must be false: true claimed an analytics consent
  // the user was never asked for.
  const handleSubmitUserPreferences = async (values: FormValues) => {
    await submitUserPreferences({
      marketingSubscribed: values.marketingSubscribed === 'yes',
      pltvAnalyticsAgreed: false,
      fallbackEmail: values.email,
    });
  };

  const handleUserExistedCheck = async (values: FormValues): Promise<boolean> => {
    try {
      const { captchaSignature, validationData } = await handleValidateRecaptcha();
      const parameters = captchaSignature
        ? {
            email: values.email,
            validationData,
            captchaSignature,
          }
        : {
            email: values.email,
            validationData,
          };

      const res = await createPublicClient.query({ 
        query: doesUserExist, 
        variables: parameters 
      });
      return res.data?.doesUserExist || false;
    } catch (err: any) {
      addErrorInDatadogRum(err);
      setServerError(err.message || "An error occurred while checking if user exists");
      return false;
    }
  };

  const signIn = async (callback: () => void) => {
    if (isFederatedSignUp) {
      // Config aws web client id for sign-in instead of federated web client id
      configAmplify(false);
    }
    const signInRequestParams: any = {
      username: formValues.email(),
      password: formValues.password(),
    };

    if (recaptchaAutomation) {
      signInRequestParams.validationData = {
        Name: "recaptchaToken",
        Value: "",
      };
      signInRequestParams.captchaSignature = {
        auth: recaptchaAutomation.auth,
        nonce: recaptchaAutomation.nonce,
      };
    } else if (recaptchaRef.current) {
      const token = await recaptchaRef.current.execute();
      signInRequestParams.validationData = {
        Name: "recaptchaToken",
        Value: token,
      };
    }
    
    try {
      await Auth.signIn(signInRequestParams);
      callback();
    } catch (error) {
      addErrorInDatadogRum(error);
      navigate(ROUTES(lang).LOGIN);
    }
  };

  const handleError = (err: string) => {
    setIsProcessing(false);
    const errorMsg = err.toLowerCase();
    if (
      errorMsg === "unknown_access_id" ||
      errorMsg === "invalid_signature" ||
      errorMsg === "link_expired" ||
      errorMsg === "link_redeemed"
    ) {
      showPopupErrorMessage(err);
    } else if (errorMsg === "existing_customer") {
      setPopup({
        visible: true,
        title: rpData.existing_account_popup_title.text.replace(
          "@first_name",
          firstNameRef.current?.value || ""
        ),
        subtitle: rpData.existing_account_popup_subtitle.raw,
      });
    } else {
      setServerError(err);
    }
  };
  
  const handleSignUp = async (response: {
    data?: any;
    errors?: readonly any[] | undefined;
    error?: ApolloError | undefined;
  }, values: FormValues) => {
    const res = isFederatedSignUp
      ? response.data?.signupFederatedUserWithSubscription
      : response.data?.signupResellerUserWithSubscription;

    if (res && res.success) {
      if (isFederatedSignUp) {
        if (rtDataConsentCheckbox) {
          await handleSubmitUserPreferences(values);
        }
        navigate(ROUTES(lang).LMOD_SURVEY, {
          state: {
            fromCreateAccount: true,
          },
        });
      } else {
        signIn(async () => {
          if (rtDataConsentCheckbox) {
            await handleSubmitUserPreferences(values);
          }
          navigate(ROUTES(lang).LMOD_SURVEY, {
            state: {
              fromCreateAccount: true,
            },
          });
        });
      }
    } else {
      const error = response.errors?.[0] || response.data?.errors?.[0];
      if (error) {
        handleError(error.message);
      } else {
        handleError("An unexpected error occurred");
      }
    }
  };

  const callSignupResellerUserWithSubscription = async (values: FormValues) => {
    const {
      email: emailValue,
      password: passwordValue,
      firstName: firstNameValue,
      lastName: lastNameValue,
      marketingSubscribed: marketingSubscribedValue,
      version: versionValue,
    } = values;
    const { captchaSignature, validationData } = await handleValidateRecaptcha();
    const input = {
      email: emailValue,
      password: passwordValue,
      givenName: firstNameValue,
      familyName: lastNameValue,
      subComms: marketingSubscribedValue === "yes",
      validationData,
      country: countryCode,
      ...(captchaSignature && { captchaSignature })
    };

    if (isFederatedSignUp) {
      try {
        const res = await createPublicClient.mutate({ 
          mutation: SignupFederatedUserWithSubscription, 
          variables: {
            input,
            accessId,
          } 
        });
        if (res && res.data) {
          handleSignUp(res as any, values);
          deleteSessionStorage(RESELLER_ACCESS_ID);
        }
      } catch (err: any) {
        addErrorInDatadogRum(err);
        handleError(
          err.errors && err.errors.length > 0 ? err.errors[0].message : err.message
        );
      }
      return;
    }
    
    try {
      const res = await createPublicClient.mutate({ 
        mutation: SignupResellerUserWithSubscription, 
        variables: {
          input,
          signed_link: signedLink as SignedLink,
          version: versionValue,
        } 
      });
      if (res && res.data) {
        handleSignUp(res as any, values);
      }
    } catch (err: any) {
      addErrorInDatadogRum(err);
      handleError(
        err.errors && err.errors.length > 0 ? err.errors[0].message : err.message
      );
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsProcessing(true);
    
    const values: FormValues = {
      email: formValues.email(),
      password: formValues.password(),
      firstName: formValues.firstName(),
      lastName: formValues.lastName(),
      marketingSubscribed: formValues.marketingSubscribed(),
      version,
    };
    
    const errors = {
      email: validators.email(values.email),
      password: validators.password(values.password),
      firstName: validators.firstName(values.firstName),
      lastName: validators.lastName(values.lastName),
      marketingSubscribed: validators.marketingSubscribed(
        values.marketingSubscribed
      ),
    };

    setInternalError(errors);

    // stop if any field validation failed
    if (Object.values(errors).some(val => !!val)) {
      setIsProcessing(false);
      return;
    }

    // check the user if it's an existing user first
    const userCheck = await handleUserExistedCheck(values);
    const followExistingUserJourney = !isFederatedSignUp && userCheck;

    if (followExistingUserJourney) {
      navigate(ROUTES(lang).LOGIN, {
        state: {
          email: values.email, // Email in signedLink was encoded so attach more original email value for LOGIN
          signedLink,
          prevSignUpUrl: location.href,
          fromResellerSignUpPage: true,
        },
      });
    } else {
      await callSignupResellerUserWithSubscription(values);
    }
  };
  return (
    <Layout
      data={prismicSignUpLayout.data}
      omitClose
      lang={lang}
      pathName={currentPagePath}
      title={rpData.page_title.text}
      scripts={[]}
    >
      <Wrapper>
        <Title
          id="create-reseller-account-title"
          variant='h5'
          style={{ marginTop: '24px', fontSize: '28px' }}
        >
          {rpData.account_title.text}
        </Title>
        <Subtitle>{rpData.account_subtitle.text}</Subtitle>
        
        <LoginLink id="have-an-account" style={{ marginBottom: '0px'}}> 
          <Subtitle>
            {rpData.existing_account_subtitle.text}
            <Link 
              to="/login"
              onClick={handleDisableLink}
              state={{ signedLink, prevSignUpUrl: location.href }}
              style={{ marginLeft: '5px', fontSize: '16px' }}
              className={isProcessing ? " disabled" : ""}
            >
              {rpData.reseller_sign_in_link_label.text}
            </Link>
          </Subtitle>
        </LoginLink>
        {serverError && <Alert severity="error" style={{ marginBottom: '20px' }} onClose={() => {
            setServerError("");
          }}><RichText render={rpData.server_error.raw} /></Alert>}
        {federatedError && <Alert severity="error" style={{ marginBottom: '20px' }} onClose={() => {
            setFederatedError("");
          }}>{federatedError}</Alert>}
        <Form id="create-reseller-account-form" component="form">
          { TextField && <TextField
            label={rpData.first_name_label.text}
            error={internalError.firstName}
            inputRef={firstNameRef}
            onKeyUp={handleOnKeyPress}
            data-testid="create-reseller-account-firstName"
            id="firstName"
            value={
              isFederatedSignUp ? federatedSignInUser.given_name || "" : undefined
            }
            disabled={
              isFederatedSignUp && federatedSignInUser.given_name
                ? true
                : isProcessing
            }
            helperText={internalError.firstName}
            style={{ padding: '0px' }}
          /> }
          { TextField && <TextField
            label={rpData.last_name_label.text}
            error={internalError.lastName}
            inputRef={lastNameRef}
            onKeyUp={handleOnKeyPress}
            data-testid="create-reseller-account-lastName"
            id="lastName"
            value={
              isFederatedSignUp ? federatedSignInUser.family_name || "" : undefined
            }
            disabled={
              isFederatedSignUp && federatedSignInUser.family_name
                ? true
                : isProcessing
            }
            helperText={internalError.lastName}
            style={{ padding: '0px' }}
          /> }
          { TextField && <TextField
            label={rpData.email_label.text}
            error={internalError.email}
            inputRef={emailRef}
            onKeyUp={handleOnKeyPress}
            defaultValue={email || undefined}
            data-testid="create-reseller-account-email"
            id="email"
            value={isFederatedSignUp ? federatedSignInUser.email || "" : undefined}
            disabled={
              isFederatedSignUp ? true : isDisableEmail ? true : isProcessing
            }
            helperText={internalError.email}
            style={{ padding: '0px' }}
          /> }
          { Password && <Password
            label={rpData.password_label.text}
            error={internalError.password}
            inputRef={passwordRef}
            onKeyUp={handleOnKeyPress}
            data-testid="create-reseller-account-password"
            id="password"
            disabled={isProcessing}
            helperText={internalError.password}
            style={{ padding: '0px' }}
          /> }
          <>
            <ReceiveEmailLabel id="receive-email-label">
              {rpData.receive_email_label.text}
            </ReceiveEmailLabel>
            <RadioGroup
              id="receive-email-radio-group"
              ariaLabel="receive-email"
              name="row-radio-buttons-group"
              currentValue={marketingSubscribed}
              handleOnChange={(event) => {
                handleReceiveEmailChange(event);
              }}
              radioData={[
                { value: 'yes', label: rpData.receive_email_yes.text },
                { value: 'no', label: rpData.receive_email_no.text },
              ]}
            />
            {internalError.marketingSubscribed && <div className="form-field-helper">{internalError.marketingSubscribed}</div>}
          </>
          { Button && <Button
            type="submit"
            id="submit-button"
            onClick={handleSubmit}
            disabled={isProcessing}
            submitting={isProcessing}
            style={{ width: '100%', marginTop: '20px' }}
            data-testid="reset-pwd-btn"
            variant="contained"
            label={isProcessing
            ? rpData.create_account_loading_button_label.text
            : rpData.create_account_continue_button_label.text}
          /> }
        </Form>
        <StyledDialog
          title={popup.title}
          open={popup.visible}
          fullWidth
          maxWidth="xs"
          onClose={() => setPopup({ visible: false, title: "", subtitle: "" })}
        >
          <DialogContent>
            {popup.subtitle}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setPopup({ visible: false, title: "", subtitle: "" })}
              variant="contained"
              label="Close"
            />
          </DialogActions>
        </StyledDialog>
        <RecaptchaV3 ref={recaptchaRef} />
      </Wrapper>
    </Layout>
  )
}

export default CreateResellerAccountPage;

export const query = graphql`
  query ($lang: String!, $uid: String!) {
    prismicSignUpLayout(lang: { eq: $lang }) {
      _previewable
      data {
        logo_white {
          alt
          gatsbyImageData(width: 133, height: 33)
        }
        logo_black {
          alt
          gatsbyImageData(width: 133, height: 33)
        }
        background_image {
          alt
          gatsbyImageData
        }
        body {
          ... on PrismicSignUpLayoutDataBodyFooterNavigation {
            items {
              nav_label {
                text
              }
              nav_link {
                text
              }
            }
          }
        }
      }
    }
    prismicCreateResellerAccountPage(lang: { eq: $lang }, uid: { eq: $uid }) {
      _previewable
      data {
        page_title {
          raw
          text
        }
        account_agreement {
          raw
          text
        }
        account_subtitle {
          raw
          text
        }
        account_title {
          raw
          text
        }
        existing_account_subtitle {
          raw
          text
        }
        reseller_sign_in_link_label {
          raw
          text
        }
        create_account_continue_button_label {
          raw
          text
        }
        create_account_loading_button_label {
          raw
          text
        }
        email_label {
          raw
          text
        }
        agree_to_privacy_policy_label {
          raw
          text
        }
        first_name_label {
          raw
          text
        }
        last_name_label {
          raw
          text
        }
        no_subscribe_marketing_info {
          raw
          text
        }
        password_label {
          raw
          text
        }
        receive_email_label {
          text
        }
        receive_email_yes {
          text
        }
        receive_email_no {
          text
        }
        subscribe_marketing_info {
          raw
          text
        }
        subscription_information_content {
          raw
          text
        }
        email_required {
          text
        }
        email_invalid {
          text
        }
        password_required {
          text
        }
        password_invalid {
          text
        }
        first_name_required {
          text
        }
        last_name_required {
          text
        }
        marketing_subscription_required {
          text
        }
        server_error {
          raw
          text
        }
        error_popup_title_link_invalid {
          raw
          text
        }
        error_popup_subtitle_link_invalid {
          raw
          text
        }
        error_popup_title_link_expired {
          raw
          text
        }
        error_popup_subtitle_link_expired {
          raw
          text
        }
        error_popup_title_link_redeemed {
          raw
          text
        }
        error_popup_subtitle_link_redeemed {
          raw
          text
        }
        not_authenticated_error {
          raw
          text
        }
        existing_account_popup_title {
          raw
          text
        }
        existing_account_popup_subtitle {
          raw
          text
        }
        existing_account_popup_button {
          raw
          text
        }
        empty_access_id_error {
          raw
          text
        }
      }
    }
  }
`;